import { useArrowSource } from '@components/ArtBoard/Arrows/hooks/useArrowSource'
import type { ArrowUI } from '@components/ArtBoard/Arrows/types'
import { thunkUpdateArrowLabel } from '@store/migrationActions/thunkActions/thunkArrowsActions'
import { artBoardActions } from '@store/reducers/artboard'
import cx from 'classnames'
import type React from 'react'
import { useLayoutEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useClickAway } from 'react-use'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import type { AppState } from '@/types'
import {
  ARROW_LABEL_DEFAULT_POSITION,
  ARROW_LABEL_MAX_LENGTH,
  ARROW_LABEL_PLACEHOLDER,
} from '../../constants'
import { placeLabel } from '../../utils'
import css from './styles.module.css'

interface Props {
  arrow: ArrowUI
}

export const ArrowLabelInput: React.FC<Props> = ({ arrow }) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()
  const { color } = useArrowSource(arrow.relation[0], arrow.colorId)

  const position = arrow.label?.position ?? ARROW_LABEL_DEFAULT_POSITION

  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const closedRef = useRef(false)
  const [text, setText] = useState(arrow.label?.text ?? '')

  useLayoutEffect(() => {
    if (ref.current) {
      placeLabel(ref.current)
    }

    inputRef.current?.focus({ preventScroll: true })
    inputRef.current?.select()
  }, [])

  const close = () => {
    closedRef.current = true
    dispatch(artBoardActions.setEditingArrowLabel(null))
  }

  const save = () => {
    if (closedRef.current) {
      return
    }

    const trimmed = text.trim()
    const label = trimmed ? { position, text: trimmed } : null

    close()
    dispatch(thunkUpdateArrowLabel({ id: arrow.id, label }))
  }

  useClickAway(ref, save)

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation()

    // Shift+Enter breaks the line; Enter that ends an IME composition isn't a save.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      save()
    }

    // As on a sticky note: Escape leaves the text, it doesn't throw it away.
    if (e.key === 'Escape') {
      save()
    }
  }

  return (
    <div
      ref={ref}
      className={cx(css.label, arrow.type === 'smooth' && css.smooth)}
      data-arrow-id={arrow.id}
      data-position={position}
      style={{ '--arrow-color': color } as React.CSSProperties}
    >
      {/* Sizes the plate; the zero-width space keeps a trailing line break's height. */}
      <span className={css.sizer}>
        {`${text || ARROW_LABEL_PLACEHOLDER}\u200b`}
      </span>
      <textarea
        ref={inputRef}
        aria-label="Arrow label"
        className={css.input}
        maxLength={ARROW_LABEL_MAX_LENGTH}
        placeholder={ARROW_LABEL_PLACEHOLDER}
        rows={1}
        value={text}
        onBlur={save}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  )
}
